import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../config/dayjs';
import { UserContext } from '../../../context/userContext';
import { ROUTES } from '../../../constants/routes';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import { useListPage } from '../../../hooks/useListPage';
import { useTicketsQuery } from '../../../hooks/useSupport';
import type {
  SupportTicketListItem,
  TicketCategory,
  TicketOrigin,
  TicketsQueryFilters,
  TicketStatus,
} from '../../../types/support';
import {
  DataTable,
  DataTableSkeleton,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { FilterBar } from '../../../components/common/FilterBar/FilterBar';
import { FilterToggle } from '../../../components/common/FilterToggle/FilterToggle';
import { StatusFilterDropdown } from '../../../components/common/StatusFilterDropdown/StatusFilterDropdown';
import { IdWithCopyCell, StatusBadgeCell } from '../components/cells';
import { BadgeStyle } from '../../../shared/enums/status';
import {
  TICKET_CATEGORY_OPTIONS,
  TICKET_ORIGIN_OPTIONS,
  TICKET_STATUS_BADGE_STYLE,
  TICKET_STATUS_OPTIONS,
  ticketCategoryLabelKey,
  ticketOriginLabelKey,
  ticketPriorityLabelKey,
  ticketStatusLabelKey,
  TICKET_PRIORITY_BADGE_STYLE,
} from './ticketMeta';
import { useSupportEvents } from '../../../hooks/useSupportEvents';
import { SectionTabs } from '../components/SectionTabs';
import { SUPPORT_SECTION } from '../../../layouts/DashboardV2Layout/dashboardSections';

interface SupportTicketFilters {
  status: TicketStatus | '';
  category: TicketCategory | '';
  origin: TicketOrigin | '';
  assignedToMe: boolean;
}

// Every conversation, newest first. This used to open on needs_human, which
// hid every chat an agent started (those are in_progress / waiting_user);
// tickets that need a human still stand out with their red badge.
const INITIAL_FILTERS: SupportTicketFilters = {
  status: '',
  category: '',
  origin: '',
  assignedToMe: false,
};

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
const SUPPORT_TABLE_MIN_WIDTH = '1280px';

const SUPPORT_COLUMNS = [
  { key: 'updatedAt', labelKey: 'dashboard.tables.support.updated', width: '10%', skeletonVariant: 'id' },
  { key: 'subject', labelKey: 'dashboard.tables.support.subject', width: '20%', cellClassName: 'cell-id', skeletonVariant: 'address' },
  { key: 'origin', labelKey: 'dashboard.tables.support.origin', width: '10%', skeletonVariant: 'status' },
  // Fits the longest status pill on one line ('Очікує користувача').
  { key: 'status', labelKey: 'dashboard.tables.support.status', width: '17%', skeletonVariant: 'status' },
  { key: 'category', labelKey: 'dashboard.tables.support.category', width: '12%', skeletonVariant: 'id' },
  { key: 'priority', labelKey: 'dashboard.tables.support.priority', width: '11%', skeletonVariant: 'status' },
  { key: 'assignedTo', labelKey: 'dashboard.tables.support.assignedTo', width: '13%', skeletonVariant: 'id' },
  { key: 'lang', labelKey: 'dashboard.tables.support.lang', width: '7%', align: 'center' },
] as const satisfies readonly DataTableColumnMeta[];

/**
 * Chats inbox: bot support conversations and chats an agent started from a
 * user's page, filterable by status, category, who started them, and whether
 * they are assigned to the current agent. Rows open
 * the transcript in `TicketDetail`.
 */
export const Support = () => {
  // Live queue: an escalation must appear the moment the bot hands over, not
  // on the next poll. The poll below stays as the fallback for a dropped
  // socket. Shared by url with the ticket page, so this is one connection.
  useSupportEvents();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const currentUserId = userContext?.user?.id;

  const {
    filters,
    handleFilterChange,
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    refresh,
  } = useListPage<SupportTicketFilters>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.SUPPORT_TICKETS,
    urlSync: true,
  });

  const queryFilters = useMemo<TicketsQueryFilters>(
    () => ({
      status: filters.status || undefined,
      category: filters.category || undefined,
      origin: filters.origin || undefined,
      assignedTo: filters.assignedToMe ? currentUserId : undefined,
    }),
    [filters, currentUserId],
  );

  const { data, isLoading, isError } = useTicketsQuery(page + 1, rowsPerPage, queryFilters);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleRowClick = (row: SupportTicketListItem) =>
    navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_SUPPORT}/${row._id}`);

  const renderers = useMemo<DataTableRenderers<SupportTicketListItem, typeof SUPPORT_COLUMNS>>(
    () => ({
      updatedAt: (ticket) => dayjs(ticket.updatedAt).fromNow(),
      subject: (ticket) => ticket.subject || '—',
      // Only admin-started chats get a chip; bot conversations (and legacy
      // tickets with no origin) are the default and stay quiet.
      origin: (ticket) =>
        ticket.origin === 'agent' ? (
          <StatusBadgeCell
            badgeClass={`status-badge-${BadgeStyle.IN_USE}`}
            dotClass={`status-dot-${BadgeStyle.IN_USE}`}
            label={t(ticketOriginLabelKey('agent'))}
          />
        ) : (
          '—'
        ),
      status: (ticket) => (
        <StatusBadgeCell
          badgeClass={`status-badge-${TICKET_STATUS_BADGE_STYLE[ticket.status]}`}
          dotClass={`status-dot-${TICKET_STATUS_BADGE_STYLE[ticket.status]}`}
          label={t(ticketStatusLabelKey(ticket.status))}
        />
      ),
      category: (ticket) => t(ticketCategoryLabelKey(ticket.category)),
      priority: (ticket) => (
        <StatusBadgeCell
          badgeClass={`status-badge-${TICKET_PRIORITY_BADGE_STYLE[ticket.priority]}`}
          dotClass={`status-dot-${TICKET_PRIORITY_BADGE_STYLE[ticket.priority]}`}
          label={t(ticketPriorityLabelKey(ticket.priority))}
        />
      ),
      assignedTo: (ticket) =>
        ticket.assignedTo && ticket.assignedTo === currentUserId ? (
          <>{t('dashboard.support.assignedToYou')}</>
        ) : (
          <IdWithCopyCell
            value={ticket.assignedTo}
            copyLabel={t('dashboard.support.copyAgentId')}
            emptyText={t('dashboard.support.unassigned')}
          />
        ),
      lang: (ticket) => ticket.lang.toUpperCase(),
    }),
    [t, currentUserId],
  );

  const errorMessage = t('dashboard.support.errorLoading');

  /** One branch per queue state; the populated table is the last one standing. */
  const renderQueue = () => {
    if (isError) {
      return <ErrorMessage message={errorMessage} onRetry={refresh} />;
    }

    if (isLoading) {
      return <DataTableSkeleton columns={SUPPORT_COLUMNS} minWidth={SUPPORT_TABLE_MIN_WIDTH} dense />;
    }

    if (items.length === 0) {
      return <EmptyState icon="support_agent" message={t('dashboard.support.empty')} />;
    }

    return (
      <DataTable
        columns={SUPPORT_COLUMNS}
        renderers={renderers}
        rows={items}
        rowKey={(ticket) => ticket._id}
        minWidth={SUPPORT_TABLE_MIN_WIDTH}
        dense
        pagination={{
          total,
          page,
          rowsPerPage,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
        onRowClick={handleRowClick}
        rowAriaLabel={(ticket) => ticket.subject || ticket._id}
      />
    );
  };

  // The page title is already rendered by the dashboard header (navConfig),
  // so the body starts straight at the filter bar like the other list pages.
  // The shared MUI TextField override paints outlined fields dark for the
  // mobile app, which is why the filters here are the dashboard's own
  // CSS-classed controls rather than MUI selects.
  return (
    <>
      <SectionTabs section={SUPPORT_SECTION} row />
      <FilterBar
        controls={
          <>
            <StatusFilterDropdown
              value={filters.status}
              options={TICKET_STATUS_OPTIONS}
              onChange={(status) => handleFilterChange({ status })}
              ariaLabel={t('dashboard.support.filterStatusAria')}
            />
            <StatusFilterDropdown
              value={filters.category}
              options={TICKET_CATEGORY_OPTIONS}
              onChange={(category) => handleFilterChange({ category })}
              ariaLabel={t('dashboard.support.filterCategoryAria')}
              iconName="category"
            />
            <StatusFilterDropdown
              value={filters.origin}
              options={TICKET_ORIGIN_OPTIONS}
              onChange={(origin) => handleFilterChange({ origin })}
              ariaLabel={t('dashboard.support.filterOriginAria')}
              iconName="forum"
            />
            <FilterToggle
              label={t('dashboard.support.assignedToMe')}
              icon="person"
              value={filters.assignedToMe}
              onChange={(assignedToMe) => handleFilterChange({ assignedToMe })}
            />
          </>
        }
        onRefresh={refresh}
        refreshAriaLabel={t('dashboard.support.refreshAria')}
      />

      <div className="content-inner" style={{ padding: '2.5rem' }}>
        {renderQueue()}
      </div>
    </>
  );
};

export default Support;
